import * as THREE from 'three';
import { ARCH_FOOT, ARCH_HEAD, DECK_HALF, PLATFORM, STAIR_HALF, type ArchRules, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The bridge's altar (docs/sendas/paladin.md, chapter 3), for the paladin's path: a stone bridge of one arch over the
 * rift between two plates of the shell, in the rain; the clouds pass beneath. Never chapter 1's black glass by a pale
 * sea, nor chapter 2's white salt, nor the warrior's snow: here everything is wet dark stone under a storm, the light
 * cold and low, the void under the arch full of moving cloud, the rain itself the place.
 *
 * - The bridge: a deck of grey flags with a round middle about the cistern, low parapets, the arch and its voussoirs
 *   going down into the rift; at both ends long stairs that come down onto the two plates, which slope away into the
 *   clouds (their scutes cut in hexes, dark and wet). The fourth altar on its pillar in the cistern, its eye red and
 *   white when it burns; the cistern's water at its marks. Along the parapets the two gutters and their gates; by the
 *   cistern the four levers; at each end two gargoyles that pour the cistern down their stairs as a white torrent.
 *   Iron lanterns along the parapets; at the upper end Varo's camp (crowbars against the wall, a brazier).
 * - `rainBridge` (the two ends, the gutters, the scenes): dusk under a storm, lightning far off now and then.
 * - `rainNight` (the campfire and the test): night, the lanterns and the altar the light; in the close the rain stops,
 *   the clouds open and the moon is in the cistern.
 * - The rain is one screen pass (a shader, not particles): streaks slanting in the wind over all, heavy over the half
 *   of the bridge where it falls (found by casting each pixel's ray onto the deck) and a grey veil there; its curtain
 *   moves across the bridge as the sim's rain does, seen coming. Rings of rain on the wet stone of that half.
 * - Marks on the stone: a band coming up seen as a red stir in the clouds at its stairs' foot; a mammoth's head down,
 *   a red ring at the stairs' head and its lane to the altar; a pale ring under each keeper the aura covers.
 *
 * Draw calls: the stone (one merged mesh), the deck (one), the plates (one), the clouds (two), the cistern's water,
 * the two gutters' water, the two torrents, the decals (one instanced), the glow layer, the sky dome and the rain (one
 * full-screen quad). No lights of its own: the board's are repainted (HOURS); lightning is a flash on those.
 * Measured on a phone profile at CPU ×4: 60 fps, about 50–64 ms/s of tasks, 39–47 calls. Trap: the pieces stand on
 * `terrain.ground` and `heights` (apply() replaces both with the bridge's own: the stairs step down), or they float over
 * the rift.
 * docs/RENDIMIENTO.md, «Reglas para lo nuevo» and «El puente bajo la lluvia».
 */

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WHITE = new THREE.Color('#ffffff');
const LAMP = new THREE.Color('#dfe9ff');
const STONE = ['#7d838c', '#737982', '#878d96', '#6c7179'];
const STONE_LIGHT = ['#a3a9b2', '#989ea7'];
const IRON = '#262a31';

export const ARCH_PLACES = new Set(['rainBridge', 'rainNight']);

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The bridge's world sizes. */
const HEAD = ARCH_HEAD * K;
const FOOT = ARCH_FOOT * K;
const DECK = DECK_HALF * K;
const STAIR = STAIR_HALF * K;
const PLAT = PLATFORM * K;
/** The stairs come down this far from the deck to their foot (world), in this many steps. */
const DROP = 3.2;
const STEPS = 12;
/** Where the plates begin (|z|), and how far they fall from there. */
const PLATE_Z = 6.2;
const CIST = 0.66;
/** The storm's cell stands this far from the middle over the half it pours on (world). */
const CELL = 5;

/** The bridge's ground height at a world point (the deck flat; the stairs down step by step; the plates beyond). */
export const archHeight = (x: number, z: number): number => {
  const az = Math.abs(z);
  if (az <= HEAD) return 0;
  if (az <= FOOT + 0.3) {
    const t = Math.min(1, (az - HEAD) / (FOOT - HEAD));
    return -Math.floor(t * STEPS + 0.5) * (DROP / STEPS);
  }
  void x;
  return plateY(az);
};

/** The plates' top, sloping down and away from the rift. */
const plateY = (az: number): number => -1.6 - Math.max(0, az - PLATE_Z) * 0.45;

const HOURS: Record<Hour, { bg: string; fog: string; near: number; far: number; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; cloud: string; rain: number }> = {
  day: { bg: '#59636f', fog: '#5d6773', near: 34, far: 110, hemi: ['#b9c6d8', '#1d2027', 1.9], key: ['#e3ebf8', 1.7], rim: ['#93a9d8', 1.3], sky: ['#353d4b', '#76818f', '#76818f'], cloud: '#808a98', rain: 1 },
  dusk: { bg: '#3f4855', fog: '#465060', near: 30, far: 100, hemi: ['#a4b3cb', '#15171d', 1.75], key: ['#d0dcf2', 1.45], rim: ['#7f98d6', 1.35], sky: ['#1f2532', '#56616f', '#56616f'], cloud: '#6c7685', rain: 1 },
  night: { bg: '#080b12', fog: '#0d121b', near: 28, far: 90, hemi: ['#6e80aa', '#08090d', 1.05], key: ['#9fb3e4', 0.75], rim: ['#6f8cf2', 1.2], sky: ['#03050a', '#121a28', '#121a28'], cloud: '#38425a', rain: 0.75 },
  dawn: { bg: '#59636f', fog: '#5d6773', near: 34, far: 110, hemi: ['#b9c6d8', '#1d2027', 1.9], key: ['#e3ebf8', 1.7], rim: ['#93a9d8', 1.3], sky: ['#353d4b', '#76818f', '#76818f'], cloud: '#aab3bf', rain: 1 },
};

type U = { uTime: THREE.IUniform<number>; uRainZ: THREE.IUniform<number>; uRain: THREE.IUniform<number>; uNight: THREE.IUniform<number> };

/** GLSL shared by the stone: a hash, value noise, and the rain's weight at a world point (the heavy half). */
const COMMON = /* glsl */ `
float arH(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float arN(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(arH(i), arH(i + vec2(1, 0)), u.x), mix(arH(i + vec2(0, 1)), arH(i + vec2(1, 1)), u.x), u.y); }
// How hard it rains here: 1 under the storm's cell (centred at uRainZ, over half the bridge), a drizzle elsewhere.
float arRain(float z) { return uRain * mix(0.3, 1.0, 1.0 - smoothstep(3.6, 6.2, abs(z - uRainZ))); }
// Rings of rain on standing water: a few cells, each a ring growing and fading, staggered.
float arRings(vec2 p, float t) {
  vec2 c = floor(p);
  float r = 0.0;
  for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
    vec2 g = c + vec2(float(x), float(y));
    float h = arH(g);
    vec2 o = g + vec2(arH(g + 3.1), arH(g + 7.7));
    float ph = fract(t * (0.8 + h * 0.6) + h * 9.0);
    float d = length(p - o);
    r += smoothstep(0.035, 0.0, abs(d - ph * 0.4)) * (1.0 - ph) * step(0.45, h);
  }
  return r;
}
`;

/**
 * Wet stone: the stone's own material, darker and glossy where it faces up (water standing on it, puddles in the low
 * spots), rings of rain where it falls hard, and runs of water down the faces that stand.
 */
const wetStone = (src: THREE.MeshStandardMaterial, u: U, flags = false): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vArW;\nvarying vec3 vArN;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvArW = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvArN = normalize(mat3(modelMatrix) * objectNormal);');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uTime;
uniform float uRainZ;
uniform float uRain;
uniform float uNight;
varying vec3 vArW;
varying vec3 vArN;
${COMMON}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float arUp = smoothstep(0.55, 0.9, vArN.y);
float arRainK = arRain(vArW.z);
float arPud = 0.0;
{
  ${
    flags
      ? `// Flags: rows of slabs, each its own shade, a dark joint between.
  vec2 q = vArW.xz * vec2(1.7, 2.3);
  q.x += floor(q.y) * 0.5;
  vec2 cell = floor(q);
  vec2 f = fract(q);
  float joint = smoothstep(0.0, 0.05, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
  diffuseColor.rgb *= (0.86 + 0.22 * arH(cell)) * (0.62 + 0.38 * joint);
  diffuseColor.rgb *= 0.93 + 0.1 * arN(vArW.xz * 3.1);`
      : `diffuseColor.rgb *= 0.9 + 0.16 * arN(vArW.xz * 2.7 + vArW.y * 1.3);`
  }
  // Puddles in the low spots of what faces up.
  arPud = smoothstep(0.55, 0.72, arN(vArW.xz * 0.8 + 11.0)) * arUp;
  // Wet: darker everywhere, darkest where water stands.
  diffuseColor.rgb *= mix(0.78, 0.55, arPud) * mix(1.0, 0.92, arRainK);
  // Water running down the standing faces, in streaks.
  float side = 1.0 - smoothstep(0.2, 0.5, abs(vArN.y));
  float run = smoothstep(0.55, 1.0, arN(vec2(dot(vArW.xz, vec2(7.0, 5.0)), vArW.y * 1.4 + uTime * 1.6)));
  diffuseColor.rgb *= 1.0 - side * run * 0.22 * arRainK;
}`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
roughnessFactor = mix(roughnessFactor, 0.12, max(arPud, arUp * 0.45));`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
{
  float rings = arRings(vArW.xz * 4.5, uTime * 1.3) * arUp * arRainK * arRainK;
  vec3 sky = mix(vec3(0.55, 0.62, 0.72), vec3(0.16, 0.2, 0.3), uNight);
  float fr = pow(1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0), 3.0);
  totalEmissiveRadiance += sky * (rings * 0.22 + fr * arPud * 0.35);
}`,
      );
  };
  m.customProgramCacheKey = () => (flags ? 'somnhold-arch-flags' : 'somnhold-arch-stone');
  m.needsUpdate = true;
  return m;
};

/** The shell's plates: dark wet scutes in a hex pattern, their grooves deep, a sheen of rain on them. */
const plateMat = (u: U): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ color: '#30353d', roughness: 0.42, metalness: 0.08 });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vArW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvArW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uTime;\nuniform float uRainZ;\nuniform float uRain;\nuniform float uNight;\nvarying vec3 vArW;\n${COMMON}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  // Hex scutes: distance to the nearest hex edge at two scales (the plates' great scutes and their ridges).
  vec2 p = vArW.xz / 3.2 + vec2(arN(vArW.xz * 0.3), arN(vArW.zx * 0.3 + 4.0)) * 0.35;
  vec2 r = vec2(1.0, 1.7320508);
  vec2 h = r * 0.5;
  vec2 a = mod(p, r) - h;
  vec2 b = mod(p - h, r) - h;
  vec2 g = dot(a, a) < dot(b, b) ? a : b;
  vec2 ag = abs(g);
  float edge = 0.5 - max(dot(ag, normalize(vec2(1.0, 1.7320508))), ag.x);
  float groove = 1.0 - smoothstep(0.0, 0.05, edge);
  float ridge = smoothstep(0.05, 0.16, edge) * (1.0 - smoothstep(0.16, 0.34, edge));
  vec2 id = p - g;
  diffuseColor.rgb *= (0.88 + 0.18 * arH(floor(id * 3.0))) * (1.0 - groove * 0.4) * (1.0 + ridge * 0.1);
  diffuseColor.rgb *= 0.86 + 0.24 * arN(vArW.xz * 0.9);
  // Growth rings in each scute, faint.
  diffuseColor.rgb *= 1.0 - 0.06 * smoothstep(0.6, 1.0, sin(edge * 60.0));
  // Darker as it falls away from the rift into the cloud.
  diffuseColor.rgb *= mix(1.0, 0.5, smoothstep(8.0, 30.0, abs(vArW.z)));
}`,
      );
  };
  m.customProgramCacheKey = () => 'somnhold-arch-plate';
  m.needsUpdate = true;
  return m;
};

/** The sea of cloud under the bridge: soft drifting heaps, lit from above, gone dark at night. */
const cloudMat = (thin: boolean): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    fog: false,
    uniforms: { time: { value: 0 }, color: { value: new THREE.Color('#8e98a6') }, shade: { value: new THREE.Color('#2a303b') }, stir: { value: new THREE.Vector3(0, 0, 0) }, open: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 color;
      uniform vec3 shade;
      uniform vec3 stir;
      uniform float open;
      varying vec3 vW;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      float fbm(vec2 p) { float a = 0.5, s = 0.0; for (int i = 0; i < 4; i++) { s += a * n(p); p = p * 2.03 + 7.1; a *= 0.5; } return s; }
      void main() {
        vec2 p = vW.xz * ${thin ? '0.07' : '0.05'} + vec2(time * ${thin ? '0.05' : '0.018'}, time * 0.006);
        float f = fbm(p);
        float lit = fbm(p * 1.7 + 3.0);
        vec3 c = mix(shade, color, smoothstep(0.3, 0.9, lit) * (0.55 + 0.45 * f));
        // A band coming up: the cloud at its stairs' foot stirs and darkens, a red glow under it.
        float d = length(vW.xz - stir.xy);
        float st = stir.z * (1.0 - smoothstep(1.0, 5.0, d));
        c = mix(c, shade * 0.6, st * 0.7);
        c += vec3(1.0, 0.31, 0.23) * st * 0.35 * (0.6 + 0.4 * sin(time * 9.0));
        ${
          thin
            ? 'float a = smoothstep(0.58, 0.85, f) * smoothstep(0.3, 0.7, n(p * 0.4 + 5.0)) * 0.4 * (1.0 - open);'
            : 'float a = mix(smoothstep(0.25, 0.65, f) * 0.95, 0.25, open) ; c = mix(c, color * 0.6, open * 0.4);'
        }
        float far = smoothstep(70.0, 120.0, length(vW.xz));
        gl_FragColor = vec4(c, a * (1.0 - far));
        #include <colorspace_fragment>
      }`,
  });

/**
 * The rain: one quad over the screen. Each pixel's ray is cast onto the deck's height to know where on the bridge it
 * looks (the heavy half or the drizzle); streaks slanting in the wind in two layers, and a grey veil where it pours.
 */
const rainMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: false,
    fog: false,
    uniforms: {
      time: { value: 0 },
      invVP: { value: new THREE.Matrix4() },
      cam: { value: new THREE.Vector3() },
      aspect: { value: 1 },
      rainZ: { value: 0 },
      rain: { value: 1 },
      night: { value: 0 },
      flash: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform mat4 invVP;
      uniform vec3 cam;
      uniform float aspect;
      uniform float rainZ;
      uniform float rain;
      uniform float night;
      uniform float flash;
      varying vec2 vUv;
      float h(float x) { return fract(sin(x * 91.7) * 43758.5453); }
      float streaks(vec2 uv, float cols, float len, float speed, float slant) {
        uv.x += uv.y * slant;
        float c = floor(uv.x * cols);
        float hc = h(c);
        float y = fract(uv.y * len + time * speed * (0.75 + 0.5 * hc) + hc * 13.0);
        float drop = smoothstep(0.0, 0.04, y) * (1.0 - smoothstep(0.04, 0.22, y));
        float fx = abs(fract(uv.x * cols) - 0.5) * 2.0;
        return drop * (1.0 - smoothstep(0.08, 0.3, fx)) * step(0.35, h(c + 17.0));
      }
      void main() {
        vec2 ndc = vUv * 2.0 - 1.0;
        vec4 a = invVP * vec4(ndc, -1.0, 1.0);
        vec4 b = invVP * vec4(ndc, 1.0, 1.0);
        vec3 p0 = a.xyz / a.w;
        vec3 p1 = b.xyz / b.w;
        vec3 d = p1 - p0;
        float t = d.y < -1e-4 ? clamp(-p0.y / d.y, 0.0, 1.0) : 1.0;
        float z = (p0 + d * t).z;
        float heavy = 1.0 - smoothstep(3.6, 6.2, abs(z - rainZ));
        float k = rain * mix(0.35, 1.0, heavy);
        vec2 uv = vec2(vUv.x * aspect, vUv.y);
        float s = streaks(uv, 90.0, 1.6, 2.6, 0.18) * 0.55 + streaks(uv * 1.7 + 0.3, 140.0, 2.4, 3.4, 0.2) * 0.35 + streaks(uv * 0.6 + 0.7, 50.0, 1.0, 1.9, 0.16) * 0.5 * heavy;
        vec3 c = mix(vec3(0.8, 0.86, 0.94), vec3(0.42, 0.5, 0.66), night);
        float alpha = s * k * 0.42 + heavy * rain * 0.12 + flash * 0.25;
        c = mix(c, vec3(0.92, 0.95, 1.0), flash);
        gl_FragColor = vec4(c, clamp(alpha, 0.0, 0.8));
        #include <colorspace_fragment>
      }`,
  });

/** Water: the cistern's (dark, the sky in it, the rain's rings, the moon) and the gutters' and torrents' (running). */
const waterMat = (kind: 'still' | 'run' | 'torrent'): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: { time: { value: 0 }, sky: { value: new THREE.Color('#56616f') }, deep: { value: new THREE.Color('#141a22') }, night: { value: 0 }, flow: { value: 0 }, moon: { value: 0 }, rain: { value: 1 }, life: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      varying vec2 vUv;
      void main() { vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 sky;
      uniform vec3 deep;
      uniform float night;
      uniform float flow;
      uniform float moon;
      uniform float rain;
      uniform float life;
      varying vec3 vW;
      varying vec2 vUv;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec3 v = normalize(cameraPosition - vW);
        float fr = pow(1.0 - clamp(v.y, 0.0, 1.0), 2.0);
        ${
          kind === 'still'
            ? `vec3 c = mix(deep, sky, 0.25 + fr * 0.6);
        // Rings of rain on it.
        vec2 p = vW.xz * 7.0;
        vec2 cl = floor(p);
        float r = 0.0;
        for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
          vec2 g = cl + vec2(float(x), float(y));
          vec2 o = g + vec2(h(g + 3.1), h(g + 7.7));
          float ph = fract(time * (0.9 + h(g) * 0.6) + h(g) * 9.0);
          r += smoothstep(0.06, 0.0, abs(length(p - o) - ph * 0.6)) * (1.0 - ph);
        }
        c += vec3(0.5, 0.56, 0.66) * r * 0.35 * rain;
        // The moon in it (the close): a pale disc, rippling.
        vec2 m = vUv - vec2(0.42, 0.58);
        m.x += sin(vUv.y * 40.0 + time * 2.0) * 0.006;
        c += vec3(0.85, 0.9, 1.0) * moon * (smoothstep(0.13, 0.1, length(m)) + smoothstep(0.32, 0.0, length(m)) * 0.25);
        c *= mix(1.0, 0.55, night * (1.0 - moon * 0.5));
        float a = 0.93;`
            : kind === 'run'
              ? `float run = n(vec2(vUv.x * 5.0, vUv.y * 10.0 - time * 3.5));
        vec3 c = mix(deep * 1.6, sky * 1.4, 0.35 + 0.45 * run * flow + fr * 0.2);
        c += vec3(0.8, 0.86, 0.94) * smoothstep(0.7, 0.95, run) * flow * 0.4;
        c *= mix(1.0, 0.55, night);
        float a = mix(0.45, 0.9, flow);`
              : `float across = 1.0 - abs(vUv.x - 0.5) * 2.0;
        float foam = n(vec2(vUv.x * 9.0, vUv.y * 5.0 - time * 7.0)) * 0.6 + n(vec2(vUv.x * 21.0, vUv.y * 12.0 - time * 11.0)) * 0.4;
        float front = smoothstep(life * 1.25 - 0.05, life * 1.25 - 0.25, 1.0 - vUv.y);
        vec3 c = mix(vec3(0.62, 0.7, 0.8), vec3(1.0), smoothstep(0.45, 0.85, foam));
        c *= mix(1.0, 0.7, night);
        float a = smoothstep(0.0, 0.25, across) * front * (0.7 + 0.3 * foam) * smoothstep(1.0, 0.85, life);`
        }
        gl_FragColor = vec4(c, a);
        #include <colorspace_fragment>
      }`,
  });

/** The decals' kinds (their quad drawn by the shader): a pulsing ring, a soft disc, a lane. */
const DECAL_RING = 0;
const DECAL_SOFT = 1;
const DECAL_LANE = 2;

export class ArchLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private dome: SkyDome | null = null;
  private u: U = { uTime: { value: 0 }, uRainZ: { value: 5 }, uRain: { value: 1 }, uNight: { value: 0 } };
  private cloud: THREE.ShaderMaterial | null = null;
  private wisp: THREE.ShaderMaterial | null = null;
  private rain: THREE.Mesh | null = null;
  private rainU: THREE.ShaderMaterial | null = null;
  private pool: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; shown: number } | null = null;
  private gutters: { mat: THREE.ShaderMaterial; gate: THREE.Mesh; up: number; flow: number }[] = [];
  private torrents: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; life: number }[] = [];
  private decals: THREE.InstancedMesh | null = null;
  private eye: { at: THREE.Vector3; mat: THREE.MeshStandardMaterial; open: number } | null = null;
  private lamps: THREE.Vector3[] = [];
  private fire: THREE.Vector3 | null = null;
  private hour: Hour = 'dusk';
  private now = 0;
  private lastTick = -1;
  /** The storm's cell over the bridge: where it stands (world z, ±`CELL` over a half) and where it goes. */
  private curtain = CELL;
  private curtainTo = CELL;
  /** A band seen in the clouds below: where, and until when (s). */
  private stir = { x: 0, z: 0, until: 0 };
  /** The close: the rain stopped, the clouds open, the moon in the cistern. */
  private lit = false;
  private flash = 0;
  private nextFlash = 6;
  private base: { hemi: number; key: number } = { hemi: 1, key: 1 };
  readonly anchors: { levers: THREE.Vector3[]; cistern: THREE.Vector3 | null; ends: THREE.Vector3[] } = { levers: [], cistern: null, ends: [] };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `arch-${place}`;
    const r = s.rules?.senda?.arch;
    this.lit = place === 'rainNight' && !!s.senda?.arch && s.senda.arch.felled >= 2;
    if (r) this.curtain = this.curtainTo = r.rain.first === 0 ? CELL : -CELL;
    const kit = new Kit();
    this.buildBridge(kit);
    this.buildStairs(kit);
    this.buildCistern(kit);
    this.buildFittings(kit, r);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.62, metalness: 0.04, flatShading: true });
    const stone = wetStone(mat, this.u);
    mat.dispose();
    this.mats.push(stone);
    const dress = kit.build(stone, 'arch-stone');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.buildDeck();
    this.buildPlates();
    this.buildWater(r);
    this.buildDecals();
    this.group.add(this.glow.mesh);
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, hour: Hour): void {
    // The bridge is its own: the board's tiles and the world beyond them hidden; the pieces stand on its stone.
    terrain.mesh.visible = false;
    terrain.decor.visible = false;
    if (terrain.fog) terrain.fog.group.visible = false;
    terrain.ground = (x, z) => archHeight(x, z);
    const R = this.map.radius;
    const W = 2 * R + 1;
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        const id = (r + R) * W + (q + R);
        if (id < 0 || id >= terrain.heights.length) continue;
        const x = (q * 1000 + r * 500) * K;
        const z = r * 866 * K;
        terrain.heights[id] = archHeight(x, z);
      }
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.buildClouds();
    this.buildRain();
    this.paint(hour);
  }

  paint(hour: Hour): void {
    this.hour = hour;
    const look = HOURS[hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog);
      scene.fog.near = look.near;
      scene.fog.far = look.far;
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.base = { hemi: look.hemi[2], key: look.key[1] };
    // The moon (the close's clear night) or nothing: the storm hides the sun.
    this.dome?.set(look.sky[0], look.sky[1], this.lit ? '#cfdcff' : look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(-0.35, 0.55, -0.75);
    const night = hour === 'night' ? 1 : 0;
    this.u.uNight.value = night;
    this.u.uRain.value = this.lit ? 0 : look.rain;
    if (this.cloud) {
      (this.cloud.uniforms.color as THREE.IUniform<THREE.Color>).value.set(look.cloud);
      (this.cloud.uniforms.shade as THREE.IUniform<THREE.Color>).value.set(night ? '#0c1018' : '#2a303b');
      (this.cloud.uniforms.open as THREE.IUniform<number>).value = this.lit ? 0.75 : 0;
    }
    if (this.wisp) {
      (this.wisp.uniforms.color as THREE.IUniform<THREE.Color>).value.set(look.cloud);
      (this.wisp.uniforms.shade as THREE.IUniform<THREE.Color>).value.set(night ? '#0c1018' : '#3a414d');
      (this.wisp.uniforms.open as THREE.IUniform<number>).value = this.lit ? 1 : 0;
    }
    for (const m of [this.pool?.mat, ...this.gutters.map((g) => g.mat), ...this.torrents.map((t) => t.mat)]) {
      if (!m) continue;
      (m.uniforms.sky as THREE.IUniform<THREE.Color>).value.set(look.sky[1]);
      (m.uniforms.night as THREE.IUniform<number>).value = night;
      (m.uniforms.rain as THREE.IUniform<number>).value = this.lit ? 0 : 1;
    }
    if (this.pool) (this.pool.mat.uniforms.moon as THREE.IUniform<number>).value = this.lit ? 1 : 0;
    if (this.rainU) {
      (this.rainU.uniforms.night as THREE.IUniform<number>).value = night;
      (this.rainU.uniforms.rain as THREE.IUniform<number>).value = this.lit ? 0 : look.rain;
    }
    if (this.rain) this.rain.visible = !this.lit;
  }

  /* ---------- the place ---------- */

  /** The deck's outline: the straight run with its round middle, as a polygon (x, z), clockwise from the upper end. */
  private outline(half: number, round: number): THREE.Vector2[] {
    const pts: THREE.Vector2[] = [];
    const zc = Math.sqrt(Math.max(0, round * round - half * half));
    pts.push(new THREE.Vector2(half, -HEAD));
    pts.push(new THREE.Vector2(half, -zc));
    const a0 = Math.atan2(-zc, half);
    const a1 = Math.atan2(zc, half);
    for (let i = 1; i < 12; i++) {
      const a = a0 + ((a1 - a0) * i) / 12;
      pts.push(new THREE.Vector2(Math.cos(a) * round, Math.sin(a) * round));
    }
    pts.push(new THREE.Vector2(half, zc));
    pts.push(new THREE.Vector2(half, HEAD));
    pts.push(new THREE.Vector2(-half, HEAD));
    pts.push(new THREE.Vector2(-half, zc));
    for (let i = 1; i < 12; i++) {
      const a = Math.PI - a1 + ((a1 - a0) * i) / 12;
      pts.push(new THREE.Vector2(Math.cos(a) * round, Math.sin(a) * round));
    }
    pts.push(new THREE.Vector2(-half, -zc));
    pts.push(new THREE.Vector2(-half, -HEAD));
    return pts;
  }

  /** The arch's body under the deck, its two faces and voussoirs, the parapets along the deck. */
  private buildBridge(kit: Kit): void {
    // The body: a side profile (z, y) with the arch cut out, run across the bridge's width.
    const W = DECK + 0.2;
    const shape = new THREE.Shape();
    const span = 6.6;
    shape.moveTo(-span, 0);
    shape.lineTo(span, 0);
    shape.lineTo(span, -13);
    shape.lineTo(5.2, -13);
    // The arch: from its springing on the right, over the top, to the left.
    for (let i = 0; i <= 24; i++) {
      const a = (i / 24) * Math.PI;
      shape.lineTo(Math.cos(a) * 5.2, -13 + Math.sin(a) * 10.2);
    }
    shape.lineTo(-span, -13);
    shape.lineTo(-span, 0);
    const body = new THREE.ExtrudeGeometry(shape, { depth: W * 2, bevelEnabled: false, curveSegments: 4 });
    // Its profile is in (x=z, y), extruded along z: turn it so the profile's x runs along the world's z.
    body.translate(0, 0, -W);
    body.rotateY(Math.PI / 2);
    kit.add(body, STONE[1] as string, place(0, -0.32, 0));
    body.dispose();
    // The round middle's balcony over corbels.
    const balc = new THREE.CylinderGeometry(PLAT + 0.18, PLAT - 0.1, 0.5, 28);
    kit.add(balc, STONE[0] as string, place(0, -0.3, 0));
    balc.dispose();
    const corbel = new THREE.BoxGeometry(0.28, 0.5, 0.42);
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      const x = Math.cos(a) * (PLAT - 0.05);
      const z = Math.sin(a) * (PLAT - 0.05);
      if (Math.abs(x) < W + 0.05) continue;
      kit.add(corbel, STONE[3] as string, place(x, -0.75, z, -a, 1, 0, 0));
    }
    corbel.dispose();
    // Voussoirs round the arch on both faces, lighter, every other one standing out.
    const vous = new THREE.BoxGeometry(0.08, 0.95, 0.62);
    for (const sx of [-1, 1])
      for (let i = 0; i <= 26; i++) {
        const a = (i / 26) * Math.PI;
        const z = Math.cos(a) * 5.62;
        const y = -13.32 + Math.sin(a) * 10.62;
        kit.add(vous, (i % 2 ? STONE_LIGHT[0] : STONE_LIGHT[1]) as string, place(sx * (W + 0.02), y, z, 0, 1, a - Math.PI / 2, 0));
      }
    vous.dispose();
    // A string course under the deck's edge.
    const course = new THREE.BoxGeometry(0.16, 0.14, span * 2 + 0.2);
    for (const sx of [-1, 1]) kit.add(course, STONE_LIGHT[1] as string, place(sx * (W + 0.06), -0.38, 0));
    course.dispose();
    // The parapets: low walls along the deck, round the middle, open at the stairs' heads.
    const out = this.outline(DECK + 0.14, PLAT + 0.14);
    const seg = new THREE.BoxGeometry(1, 0.34, 0.2);
    const cap = new THREE.BoxGeometry(1, 0.06, 0.26);
    for (let i = 0; i < out.length; i++) {
      const a = out[i] as THREE.Vector2;
      const b = out[(i + 1) % out.length] as THREE.Vector2;
      // (Not across the heads.)
      if (Math.abs(a.y) === HEAD && Math.abs(b.y) === HEAD) continue;
      const len = a.distanceTo(b);
      if (len < 0.01) continue;
      const yaw = Math.atan2(b.x - a.x, b.y - a.y) + Math.PI / 2;
      const mx = (a.x + b.x) / 2;
      const mz = (a.y + b.y) / 2;
      kit.add(seg, STONE[(i & 1) as 0 | 1] as string, place(mx, 0.17, mz, yaw, new THREE.Vector3(len + 0.02, 1, 1)));
      kit.add(cap, STONE_LIGHT[0] as string, place(mx, 0.37, mz, yaw, new THREE.Vector3(len + 0.04, 1, 1)));
    }
    seg.dispose();
    cap.dispose();
    // Newel posts at the heads, a lantern on each.
    const newel = new THREE.BoxGeometry(0.34, 0.62, 0.34);
    const lampBox = new THREE.BoxGeometry(0.16, 0.2, 0.16);
    for (const sz of [-1, 1])
      for (const sx of [-1, 1]) {
        kit.add(newel, STONE_LIGHT[1] as string, place(sx * (DECK + 0.14), 0.31, sz * HEAD));
        kit.add(lampBox, IRON, place(sx * (DECK + 0.14), 0.74, sz * HEAD));
        this.lamps.push(new THREE.Vector3(sx * (DECK + 0.14), 0.74, sz * HEAD));
      }
    // Lanterns on iron arms along the middle's balcony.
    const arm = new THREE.BoxGeometry(0.05, 0.7, 0.05);
    for (const a of [0.5, Math.PI - 0.5, Math.PI + 0.5, -0.5]) {
      const x = Math.cos(a) * (PLAT + 0.14);
      const z = Math.sin(a) * (PLAT + 0.14);
      kit.add(arm, IRON, place(x, 0.55, z));
      kit.add(lampBox, IRON, place(x, 0.95, z));
      this.lamps.push(new THREE.Vector3(x, 0.95, z));
    }
    arm.dispose();
    lampBox.dispose();
    newel.dispose();
  }

  /** The stairs at both ends, step by step down onto the plates, with their low walls and their gargoyles. */
  private buildStairs(kit: Kit): void {
    const depth = (FOOT - HEAD) / STEPS;
    const step = new THREE.BoxGeometry(STAIR * 2 + 0.1, 0.4, depth + 0.02);
    const wall = new THREE.BoxGeometry(0.2, 0.36, depth + 0.02);
    const under = new THREE.BoxGeometry(STAIR * 2 + 0.5, 1, depth + 0.02);
    for (const sz of [-1, 1]) {
      for (let i = 0; i < STEPS; i++) {
        const z = sz * (HEAD + depth * (i + 0.5));
        const top = archHeight(0, z);
        kit.add(step, (i % 2 ? STONE[0] : STONE[2]) as string, place(0, top - 0.2, z));
        // What carries it, down to the plate (deeper over the rift).
        const floor = Math.min(top - 0.4, Math.abs(z) < PLATE_Z ? -9 : plateY(Math.abs(z)) - 0.3);
        const hgt = top - 0.4 - floor + 0.4;
        if (hgt > 0.05) kit.add(under, STONE[3] as string, place(0, (top - 0.4 + floor) / 2, z, 0, new THREE.Vector3(1, hgt, 1)));
        for (const sx of [-1, 1]) kit.add(wall, STONE[1] as string, place(sx * (STAIR + 0.18), top + 0.12, z));
      }
      // The gargoyles at the head, two, crouched on the newels' outer side, their mouths over the top step.
      for (const sx of [-1, 1]) this.gargoyle(kit, sx * (STAIR + 0.05), sz * (HEAD + 0.12), sz);
    }
    step.dispose();
    wall.dispose();
    under.dispose();
  }

  /** A gargoyle: a crouched beast of dark stone, its jaws open down the stairs. */
  private gargoyle(kit: Kit, x: number, z: number, dir: number): void {
    const dark = '#4f555e';
    const yaw = dir > 0 ? 0 : Math.PI;
    const body = new THREE.BoxGeometry(0.34, 0.3, 0.5);
    const head = new THREE.BoxGeometry(0.28, 0.24, 0.3);
    const jaw = new THREE.BoxGeometry(0.22, 0.07, 0.24);
    const horn = new THREE.ConeGeometry(0.05, 0.22, 5);
    const wing = new THREE.BoxGeometry(0.04, 0.32, 0.36);
    kit.add(body, dark, place(x, 0.55, z, yaw, 1, -0.25, 0));
    kit.add(head, dark, place(x, 0.66, z + dir * 0.32, yaw, 1, 0.35, 0));
    kit.add(jaw, '#3f444c', place(x, 0.52, z + dir * 0.42, yaw, 1, 0.6, 0));
    for (const s of [-1, 1]) {
      kit.add(horn, dark, place(x + s * 0.09, 0.84, z + dir * 0.24, yaw, 1, -0.5, s * 0.3));
      kit.add(wing, dark, place(x + s * 0.2, 0.68, z - dir * 0.05, yaw, 1, -0.3, s * 0.5));
    }
    body.dispose();
    head.dispose();
    jaw.dispose();
    horn.dispose();
    wing.dispose();
  }

  /** The cistern: a round rim in the deck, its wall going down, the altar on its pillar in the middle. */
  private buildCistern(kit: Kit): void {
    const rim = new THREE.CylinderGeometry(CIST + 0.14, CIST + 0.18, 0.16, 26, 1, true);
    kit.add(rim, STONE_LIGHT[0] as string, place(0, 0.08, 0));
    rim.dispose();
    const lip = new THREE.TorusGeometry(CIST + 0.07, 0.07, 4, 28);
    kit.add(lip, STONE_LIGHT[1] as string, place(0, 0.16, 0, 0, 1, Math.PI / 2, 0));
    lip.dispose();
    const wall = new THREE.CylinderGeometry(CIST, CIST, 0.95, 26, 1, true);
    const inv = wall.clone();
    inv.scale(-1, 1, 1);
    kit.add(inv, '#3c4148', place(0, -0.47, 0));
    wall.dispose();
    inv.dispose();
    const bottom = new THREE.CircleGeometry(CIST, 20).rotateX(-Math.PI / 2);
    kit.add(bottom, '#2c3037', place(0, -0.93, 0));
    bottom.dispose();
    const pillar = new THREE.CylinderGeometry(0.17, 0.22, 1.2, 8);
    kit.add(pillar, STONE[2] as string, place(0, -0.35, 0));
    pillar.dispose();
    this.altar(kit, new THREE.Vector3(0, 0.25, 0), 0, 0.72);
    this.anchors.cistern = new THREE.Vector3(0, 1.4, 0);
  }

  /** An altar of the first Vigil (the chapters' shape): pale stone on a plinth, its eye high on its face. */
  private altar(kit: Kit, at: THREE.Vector3, face: number, scale: number): void {
    const plinth = new THREE.CylinderGeometry(0.42 * scale, 0.48 * scale, 0.14 * scale, 6);
    const stele = new THREE.BoxGeometry(0.5 * scale, 0.98 * scale, 0.24 * scale);
    const top = new THREE.ConeGeometry(0.36 * scale, 0.26 * scale, 4);
    kit.add(plinth, '#8a9099', place(at.x, at.y + 0.07 * scale, at.z, face + Math.PI / 6));
    kit.add(stele, '#c3c8cf', place(at.x, at.y + 0.63 * scale, at.z, face));
    kit.add(top, '#cfd3d9', place(at.x, at.y + 1.25 * scale, at.z, face + Math.PI / 4, new THREE.Vector3(1, 1, 0.48)));
    plinth.dispose();
    stele.dispose();
    top.dispose();
    const fx = Math.sin(face);
    const fz = Math.cos(face);
    const eyeAt = new THREE.Vector3(at.x + fx * 0.128 * scale, at.y + 0.86 * scale, at.z + fz * 0.128 * scale);
    const lid = new THREE.TorusGeometry(0.16 * scale, 0.028 * scale, 4, 18);
    lid.scale(1, 0.5, 1);
    kit.add(lid, '#3a3d45', place(eyeAt.x, eyeAt.y, eyeAt.z, face));
    lid.dispose();
    const mat = new THREE.MeshStandardMaterial({ color: '#1c1d22', emissive: new THREE.Color('#000000'), roughness: 0.4 });
    this.mats.push(mat);
    const geo = new THREE.CircleGeometry(0.068 * scale, 16);
    this.geos.push(geo);
    const iris = new THREE.Mesh(geo, mat);
    iris.position.set(eyeAt.x + fx * 0.006, eyeAt.y, eyeAt.z + fz * 0.006);
    iris.rotation.y = face;
    this.group.add(iris);
    this.eye = { at: eyeAt, mat, open: 0 };
  }

  /** The gutters' channels and gates, the levers by the cistern, Varo's camp at the upper end. */
  private buildFittings(kit: Kit, r: ArchRules | undefined): void {
    // The gutters: a channel along the parapet from each head, then across to the cistern.
    const rail = new THREE.BoxGeometry(0.05, 0.08, 1);
    for (const [sx, sz] of [
      [1, 1],
      [-1, -1],
    ] as const) {
      const x = sx * (DECK - 0.22);
      const z0 = sz * (HEAD - 0.1);
      const z1 = sz * 1.55;
      for (const o of [-0.15, 0.15]) kit.add(rail, STONE_LIGHT[1] as string, place(x + o, 0.04, (z0 + z1) / 2, 0, new THREE.Vector3(1, 1, Math.abs(z0 - z1))));
      // Across to the rim.
      const ax = sx * 0.62;
      const az = sz * 0.6;
      const len = Math.hypot(x - ax, z1 - az);
      const yaw = Math.atan2(x - ax, z1 - az);
      for (const o of [-0.15, 0.15]) kit.add(rail, STONE_LIGHT[1] as string, place((x + ax) / 2 + Math.cos(yaw) * o, 0.04, (z1 + az) / 2 - Math.sin(yaw) * o, yaw, new THREE.Vector3(1, 1, len)));
    }
    rail.dispose();
    // The levers: a gate's wheel on a post, a gargoyle's chain on a ring post.
    const post = new THREE.BoxGeometry(0.1, 0.62, 0.1);
    const wheel = new THREE.TorusGeometry(0.15, 0.025, 4, 12);
    const ring = new THREE.TorusGeometry(0.09, 0.025, 4, 10);
    const chain = new THREE.BoxGeometry(0.03, 0.03, 1);
    (r?.levers ?? []).forEach((p, i) => {
      const w = this.ctx.at(p.x, p.y, 0);
      w.y = 0;
      this.anchors.levers.push(w.clone().setY(0.95));
      kit.add(post, IRON, place(w.x, 0.31, w.z));
      if (i < 2) kit.add(wheel, '#3b4049', place(w.x, 0.66, w.z, Math.atan2(w.x, w.z)));
      else {
        kit.add(ring, '#3b4049', place(w.x, 0.66, w.z));
        // The chain runs off along the deck toward its gargoyle's head.
        const tz = i === 2 ? HEAD : -HEAD;
        const tx = w.x;
        const len = Math.abs(tz - w.z);
        kit.add(chain, '#2f343c', place(tx, 0.05, (w.z + tz) / 2, 0, new THREE.Vector3(1, 1, len)));
      }
    });
    post.dispose();
    wheel.dispose();
    ring.dispose();
    chain.dispose();
    // The ends' anchors (for the HUD's warnings).
    this.anchors.ends = [new THREE.Vector3(0, 0.6, HEAD + 2.2), new THREE.Vector3(0, 0.6, -HEAD - 2.2)];
    // Varo's camp at the upper end: crowbars against the parapet, a bedroll, a brazier.
    const bar = new THREE.BoxGeometry(0.035, 0.035, 0.95);
    for (let i = 0; i < 3; i++) kit.add(bar, '#30353d', place(-DECK + 0.08, 0.38, -HEAD + 0.6 + i * 0.12, 0, 1, -1.15, 0.1 * i));
    bar.dispose();
    const roll = new THREE.CylinderGeometry(0.1, 0.1, 0.6, 7);
    kit.add(roll, '#4a4f58', place(DECK - 0.35, 0.1, -HEAD + 0.7, 0, 1, 0, Math.PI / 2));
    roll.dispose();
    const brazier = new THREE.CylinderGeometry(0.16, 0.1, 0.22, 8);
    kit.add(brazier, IRON, place(-DECK + 0.4, 0.3, -HEAD + 1.4));
    const legs = new THREE.BoxGeometry(0.03, 0.2, 0.03);
    for (const [a, b] of [
      [0.08, 0],
      [-0.08, 0],
      [0, 0.08],
    ])
      kit.add(legs, IRON, place(-DECK + 0.4 + (a as number), 0.1, -HEAD + 1.4 + (b as number)));
    legs.dispose();
    brazier.dispose();
    this.fireAt = new THREE.Vector3(-DECK + 0.4, 0.45, -HEAD + 1.4);
  }

  private fireAt: THREE.Vector3 | null = null;

  /** The deck's flags (wet, its own shader), with the cistern's hole. */
  private buildDeck(): void {
    const shape = new THREE.Shape(this.outline(DECK + 0.04, PLAT + 0.04));
    const hole = new THREE.Path();
    hole.absarc(0, 0, CIST + 0.14, 0, Math.PI * 2, false);
    shape.holes.push(hole);
    const geo = new THREE.ShapeGeometry(shape, 18);
    geo.rotateX(Math.PI / 2);
    this.geos.push(geo);
    const base = new THREE.MeshStandardMaterial({ color: '#8c929b', roughness: 0.6, metalness: 0.04 });
    const m = wetStone(base, this.u, true);
    base.dispose();
    m.side = THREE.DoubleSide;
    this.mats.push(m);
    const deck = new THREE.Mesh(geo, m);
    deck.position.y = 0.002;
    deck.receiveShadow = true;
    deck.name = 'arch-deck';
    this.group.add(deck);
  }

  /** The two plates of the shell the bridge spans, sloping away into the cloud; their cliffs into the rift. */
  private buildPlates(): void {
    const parts: THREE.BufferGeometry[] = [];
    const low = this.ctx.low;
    for (const sz of [-1, 1]) {
      const g = new THREE.PlaneGeometry(110, 60, low ? 24 : 40, low ? 14 : 22);
      g.rotateX(-Math.PI / 2);
      const pos = g.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const z = PLATE_Z + (pos.getZ(i) + 30);
        const bump = Math.sin(x * 0.21 + z * 0.13) * 0.35 + Math.sin(x * 0.07 - z * 0.31) * 0.5;
        // Flat where the stairs come down (they rest on it), rolling farther off.
        const k = Math.min(1, Math.max(0, (Math.abs(x) - 2) / 6));
        pos.setXYZ(i, x, plateY(z) + bump * k - 0.05, sz * z);
      }
      parts.push(g.toNonIndexed());
      g.dispose();
      // The cliff: down from the plate's edge into the cloud, ragged.
      const c = new THREE.PlaneGeometry(110, 16, low ? 30 : 56, 6);
      const cp = c.getAttribute('position') as THREE.BufferAttribute;
      const top = plateY(PLATE_Z) - 0.05;
      for (let i = 0; i < cp.count; i++) {
        const x = cp.getX(i);
        const v = (cp.getY(i) + 8) / 16;
        const y = top - (1 - v) * 16;
        const jag = (Math.sin(x * 1.3) * 0.25 + Math.sin(x * 0.37 + y * 0.8) * 0.5) * (1 - v);
        cp.setXYZ(i, x, y, sz * (PLATE_Z + jag - 0.05));
      }
      parts.push(c.toNonIndexed());
      c.dispose();
    }
    const merged = mergeAll(parts);
    for (const p of parts) p.dispose();
    if (!merged) return;
    merged.computeVertexNormals();
    this.geos.push(merged);
    // (Two-sided: the mirrored plate and the cliffs face either way; three turns their normals to the eye.)
    const m = plateMat(this.u);
    m.side = THREE.DoubleSide;
    this.mats.push(m);
    const mesh = new THREE.Mesh(merged, m);
    mesh.receiveShadow = true;
    mesh.name = 'arch-plates';
    this.group.add(mesh);
  }

  /** The cistern's water; the gutters' running water and their gates; the two torrents down the stairs. */
  private buildWater(r: ArchRules | undefined): void {
    const pg = new THREE.CircleGeometry(CIST, 28).rotateX(-Math.PI / 2);
    // (Its uv in the plane, for the moon's disc.)
    this.geos.push(pg);
    const pm = waterMat('still');
    this.mats.push(pm);
    const pool = new THREE.Mesh(pg, pm);
    pool.renderOrder = 2;
    pool.name = 'arch-cistern';
    this.group.add(pool);
    this.pool = { mesh: pool, mat: pm, shown: -1 };
    // The gutters: a ribbon each, from its head down the parapet and across to the cistern.
    const gateGeo = new THREE.BoxGeometry(0.34, 0.24, 0.05);
    this.geos.push(gateGeo);
    const gateMat = new THREE.MeshStandardMaterial({ color: '#3b3f46', roughness: 0.6, metalness: 0.3, flatShading: true });
    this.mats.push(gateMat);
    for (const [sx, sz] of [
      [1, 1],
      [-1, -1],
    ] as const) {
      const x = sx * (DECK - 0.22);
      const pts = [new THREE.Vector3(x, 0.03, sz * (HEAD - 0.05)), new THREE.Vector3(x, 0.03, sz * 1.55), new THREE.Vector3(sx * 0.62, 0.03, sz * 0.6)];
      const g = ribbon(pts, 0.24);
      this.geos.push(g);
      const m = waterMat('run');
      this.mats.push(m);
      const mesh = new THREE.Mesh(g, m);
      mesh.renderOrder = 2;
      this.group.add(mesh);
      const gate = new THREE.Mesh(gateGeo, gateMat);
      const lever = r?.levers[sx > 0 ? 0 : 1];
      const lz = lever ? lever.y * K : sz * 1.32;
      gate.position.set(x, 0.12, sz * Math.max(1.6, Math.abs(lz) + 0.25));
      gate.castShadow = true;
      this.group.add(gate);
      this.gutters.push({ mat: m, gate, up: 0, flow: 0 });
    }
    // The torrents: a sheet laid down each staircase, from its head to its foot.
    for (const sz of [1, -1]) {
      const g = new THREE.PlaneGeometry(STAIR * 2 + 0.1, 1, 1, STEPS * 2);
      g.rotateX(-Math.PI / 2);
      const pos = g.getAttribute('position') as THREE.BufferAttribute;
      const uv = g.getAttribute('uv') as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const t = uv.getY(i);
        const z = sz * (HEAD + (FOOT - HEAD) * (1 - t));
        pos.setXYZ(i, pos.getX(i), archHeight(0, z) + 0.06 + Math.abs((((1 - t) * STEPS) % 1) - 0.5) * 0.05, z);
        uv.setY(i, sz > 0 ? t : t);
      }
      g.computeVertexNormals();
      this.geos.push(g);
      const m = waterMat('torrent');
      this.mats.push(m);
      const mesh = new THREE.Mesh(g, m);
      mesh.renderOrder = 3;
      mesh.visible = false;
      this.group.add(mesh);
      this.torrents.push({ mesh, mat: m, life: 0 });
    }
  }

  private buildClouds(): void {
    const g = new THREE.PlaneGeometry(260, 260, 1, 1).rotateX(-Math.PI / 2);
    this.geos.push(g);
    const m = cloudMat(false);
    this.cloud = m;
    this.mats.push(m);
    const sea = new THREE.Mesh(g, m);
    sea.position.y = -10.5;
    sea.renderOrder = -2;
    sea.frustumCulled = false;
    this.group.add(sea);
    const w = cloudMat(true);
    this.wisp = w;
    this.mats.push(w);
    const wisps = new THREE.Mesh(g, w);
    wisps.position.y = -6.5;
    wisps.renderOrder = -1;
    wisps.frustumCulled = false;
    this.group.add(wisps);
  }

  private buildRain(): void {
    const g = new THREE.PlaneGeometry(2, 2);
    this.geos.push(g);
    const m = rainMat();
    this.rainU = m;
    this.mats.push(m);
    const mesh = new THREE.Mesh(g, m);
    mesh.frustumCulled = false;
    mesh.renderOrder = 50;
    mesh.name = 'arch-rain';
    this.rain = mesh;
    this.group.add(mesh);
  }

  private buildDecals(): void {
    const q = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.geos.push(q);
    const kind = new THREE.InstancedBufferAttribute(new Float32Array(32), 1);
    const alpha = new THREE.InstancedBufferAttribute(new Float32Array(32), 1);
    q.setAttribute('aKind', kind);
    q.setAttribute('aAlpha', alpha);
    const m = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -6,
      polygonOffsetUnits: -6,
      uniforms: { time: { value: 0 } },
      vertexShader: /* glsl */ `
        attribute float aKind;
        attribute float aAlpha;
        varying float vKind;
        varying float vAlpha;
        varying vec2 vUv;
        varying vec3 vC;
        void main() {
          vKind = aKind; vAlpha = aAlpha; vUv = uv; vC = instanceColor;
          gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float time;
        varying float vKind;
        varying float vAlpha;
        varying vec2 vUv;
        varying vec3 vC;
        void main() {
          vec2 p = vUv - 0.5;
          float r = length(p) * 2.0;
          float a = 0.0;
          if (vKind < 0.5) {
            float ring = 1.0 - smoothstep(0.0, 0.1, abs(r - 0.82));
            a = (ring + (1.0 - smoothstep(0.0, 0.82, r)) * 0.25) * (0.7 + 0.3 * sin(time * 10.0));
          } else if (vKind < 1.5) {
            // A soft pale ring (a keeper under the aura).
            a = smoothstep(0.0, 0.5, 1.0 - abs(r - 0.82) * 5.0) * 0.9 + (1.0 - smoothstep(0.0, 0.8, r)) * 0.12;
          } else {
            // A lane: along its length (uv.y), chevrons running toward its far end.
            float across = 1.0 - smoothstep(0.32, 0.5, abs(p.x));
            float chev = fract(vUv.y * 6.0 + abs(p.x) * 1.4 - time * 2.2);
            a = across * (0.3 + 0.7 * smoothstep(0.55, 0.8, chev));
          }
          gl_FragColor = vec4(vC, clamp(a, 0.0, 1.0) * vAlpha);
          #include <colorspace_fragment>
        }`,
    });
    this.mats.push(m);
    const im = new THREE.InstancedMesh(q, m, 32);
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(32 * 3), 3);
    im.count = 0;
    im.frustumCulled = false;
    im.renderOrder = 5;
    im.name = 'arch-decals';
    this.decals = im;
    this.group.add(im);
  }

  /** The campfire's scene: a fire there (its light a glow). */
  setFire(p: THREE.Vector3 | null): void {
    this.fire = p;
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    if (s.tick === this.lastTick) return;
    this.lastTick = s.tick;
    const w = s.senda?.arch;
    const r = s.rules?.senda?.arch;
    if (w && r) {
      // The storm's cell over the half it pours on; seen moving to the other as the warning goes out.
      const here = w.rain === 0 ? CELL : -CELL;
      this.curtainTo = s.waveT >= w.rainAt - r.rain.warn ? -here : here;
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'gargoyle') {
        const t = this.torrents[e.n === 0 ? 0 : 1];
        if (t) {
          t.life = 0.001;
          t.mesh.visible = true;
        }
        const z = (e.n === 0 ? 1 : -1) * (HEAD + 0.3);
        this.ctx.vfx.ring(new THREE.Vector3(0, 0.1, z), 0.3, 2.4, '#dfe9f5', 6, 0.9);
        this.ctx.shake(0.25);
      }
      if (e.kind === 'bandWarn') {
        const z = e.n === 1 ? -(FOOT + 3) : e.n === 0 ? FOOT + 3 : 0;
        this.stir = { x: 0, z, until: this.now + 3.2 };
        if (e.n === 2) this.stir = { x: 0, z: 0, until: this.now + 3.2 };
      }
      if (e.kind === 'crush' || (e.kind === 'out' && e.n === 1)) this.ctx.shake(0.5);
    }
  }

  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private up = new THREE.Vector3(0, 1, 0);

  draw(s: BattleState): void {
    const r = s.rules?.senda?.arch;
    const w = s.senda?.arch;
    // The cistern's water at its marks (the close: full, the moon in it).
    if (this.pool) {
      const cap = r?.cistern?.cap ?? 8;
      const lv = this.lit ? cap : r?.cistern ? (w?.level ?? r.cistern.start) : 2;
      const k = Math.max(0, Math.min(1, lv / cap));
      this.pool.shown = this.pool.shown < 0 ? k : this.pool.shown + (k - this.pool.shown) * 0.12;
      this.pool.mesh.position.y = -0.9 + this.pool.shown * 0.98;
      this.pool.mesh.visible = this.pool.shown > 0.01;
    }
    // The gutters: running where the gate is open and it pours, still otherwise; the gate up when open.
    this.gutters.forEach((g, i) => {
      const open = !!w && w.open[i] === 1;
      const wet = !!w && w.rain === i;
      const blocked = !!w && w.blocked[i] === 1;
      const flow = open && wet && !blocked ? 1 : open && !wet ? 0.5 : 0;
      g.flow += (flow - g.flow) * 0.15;
      (g.mat.uniforms.flow as THREE.IUniform<number>).value = g.flow;
      g.up += ((open ? 1 : 0) - g.up) * 0.25;
      g.gate.position.y = 0.12 + g.up * 0.28;
    });
    // The altar's eye: dim before the cistern fills (the two ends); filling with it (the gutters); burning while it
    // holds half, flickering below it (the night); open in the close.
    if (this.eye) {
      let open = 0;
      if (this.lit) open = 1;
      else if (r?.kind === 'gutters' && r.cistern) open = s.result === 'won' ? 1 : ((w?.level ?? 0) / r.cistern.cap) * 0.6;
      else if (r?.kind === 'vigil' && r.cistern) open = w && w.level >= r.cistern.lit ? 1 : w && !w.out ? 0.35 + 0.3 * Math.abs(Math.sin(this.now * 9)) : 0;
      else if (this.place === 'rainNight') open = 1;
      this.eye.open += (open - this.eye.open) * 0.1;
    }
    this.drawDecals(s, r, w);
  }

  private drawDecals(s: BattleState, r: ArchRules | undefined, w: NonNullable<BattleState['senda']>['arch'] | undefined): void {
    const d = this.decals;
    if (!d) return;
    const kind = d.geometry.getAttribute('aKind') as THREE.InstancedBufferAttribute;
    const alpha = d.geometry.getAttribute('aAlpha') as THREE.InstancedBufferAttribute;
    let n = 0;
    const put = (x: number, z: number, y: number, sx: number, sz: number, yaw: number, k: number, a: number, col: THREE.Color): void => {
      if (n >= 32) return;
      this.q4.setFromAxisAngle(this.up, yaw);
      this.m4.compose(this.v3.set(x, y, z), this.q4, this.s3.set(sx, 1, sz));
      d.setMatrixAt(n, this.m4);
      d.setColorAt(n, col);
      kind.setX(n, k);
      alpha.setX(n, a);
      n++;
    };
    if (r && w && s.senda) {
      // The keepers the aura covers: a pale ring at their feet.
      const pale = this.c3.set('#e8eef8');
      s.senda.npcs.forEach((p, i) => {
        if (!w.covered[i] || p.hp <= 0) return;
        const at = this.ctx.at(p.x, p.y, 0);
        put(at.x, at.z, at.y + 0.04, 1.3, 1.3, 0, DECAL_SOFT, 0.55, pale);
      });
      // A mammoth's head down at the stairs' head: a red ring there and its lane to the altar.
      if (r.mammoth)
        for (let k = 0; k < 2; k++) {
          if ((w.mammoth[k] as number) <= 0 || w.mode[k] !== 1) continue;
          const z = (k === 0 ? 1 : -1) * HEAD;
          put(0, z, 0.05, 2.6, 2.6, 0, DECAL_RING, 1, RED);
          put(0, z / 2, 0.04, 1.4, Math.abs(z), k === 0 ? Math.PI : 0, DECAL_LANE, 0.8, RED);
        }
      // A band coming up: a red ring at its stairs' foot while it is seen below.
      if (this.stir.until > this.now && this.stir.z !== 0) {
        const z = Math.sign(this.stir.z) * (FOOT - 0.4);
        put(0, z, archHeight(0, z) + 0.06, 2.2, 2.2, 0, DECAL_RING, 0.9, RED_SOFT);
      } else if (this.stir.until > this.now) {
        for (const sz of [-1, 1]) put(0, sz * (FOOT - 0.4), archHeight(0, sz * (FOOT - 0.4)) + 0.06, 2.2, 2.2, 0, DECAL_RING, 0.9, RED_SOFT);
      }
    }
    d.count = n;
    d.instanceMatrix.needsUpdate = true;
    if (d.instanceColor) d.instanceColor.needsUpdate = true;
    kind.needsUpdate = true;
    alpha.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now > 0 ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    this.u.uTime.value = now;
    // The storm's cell moves across the bridge to the half it will pour on (in about the warning's time).
    const step = dt * ((2 * CELL) / 4.5);
    this.curtain += Math.max(-step, Math.min(step, this.curtainTo - this.curtain));
    this.u.uRainZ.value = this.curtain;
    for (const m of [this.cloud, this.wisp, this.pool?.mat, this.decals?.material as THREE.ShaderMaterial | undefined, ...this.gutters.map((g) => g.mat), ...this.torrents.map((t) => t.mat)]) {
      const t = m?.uniforms?.time as THREE.IUniform<number> | undefined;
      if (t) t.value = now;
    }
    const stirK = this.stir.until > now ? Math.min(1, (this.stir.until - now) * 1.2) : 0;
    if (this.cloud) (this.cloud.uniforms.stir as THREE.IUniform<THREE.Vector3>).value.set(this.stir.x, this.stir.z === 0 ? FOOT + 3 : this.stir.z, stirK);
    for (const t of this.torrents) {
      if (t.life <= 0) continue;
      t.life += dt / 1.8;
      (t.mat.uniforms.life as THREE.IUniform<number>).value = t.life;
      if (t.life >= 1) {
        t.life = 0;
        t.mesh.visible = false;
      }
    }
    // The rain over the screen: its ray reconstruction from this frame's camera.
    if (this.rainU && camera instanceof THREE.PerspectiveCamera) {
      const u = this.rainU.uniforms;
      (u.invVP as THREE.IUniform<THREE.Matrix4>).value.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).invert();
      (u.aspect as THREE.IUniform<number>).value = camera.aspect;
      (u.time as THREE.IUniform<number>).value = now;
      (u.rainZ as THREE.IUniform<number>).value = this.curtain;
    }
    // Lightning far off now and then (not at the clear close): a flash on the board's own lights.
    if (!this.lit && this.hour !== 'night' && now > this.nextFlash) {
      this.flash = 1;
      this.nextFlash = now + 9 + Math.random() * 14;
    }
    if (this.flash > 0) {
      this.flash = Math.max(0, this.flash - dt * 3.2);
      const f = this.flash * this.flash;
      if (this.ctx.hemi) this.ctx.hemi.intensity = this.base.hemi + f * 1.6;
      if (this.ctx.key) this.ctx.key.intensity = this.base.key + f * 1.2;
      if (this.rainU) (this.rainU.uniforms.flash as THREE.IUniform<number>).value = f * 0.6;
    }
    this.glow.begin();
    const night = this.hour === 'night' ? 1 : 0.55;
    this.lamps.forEach((l, i) => {
      const f = 0.85 + 0.15 * Math.sin(now * (3 + i * 0.7) + i);
      this.glow.add(l.x, l.y, l.z, LAMP, 1.0 + night * 0.5, (0.45 + 0.4 * night) * f);
    });
    const fire = this.fire ?? (this.place === 'rainNight' ? this.fireAt : null);
    if (fire) {
      const f = 0.8 + 0.2 * Math.sin(now * 9) * Math.sin(now * 5.3);
      this.glow.add(fire.x, fire.y + 0.2, fire.z, new THREE.Color('#ffb070'), this.fire ? 3.2 : 1.6, (this.fire ? 0.65 : 0.5) * f);
    }
    if (this.eye) {
      const k = this.eye.open;
      this.eye.mat.emissive.copy(RED).lerp(WHITE, 0.55).multiplyScalar(k * 1.6);
      this.eye.mat.color.set('#1c1d22').lerp(WHITE, k);
      if (k > 0.03) {
        const e = this.eye.at;
        this.glow.add(e.x, e.y, e.z, RED_SOFT, 0.9 + 0.8 * k, 0.5 * k);
        this.glow.add(e.x, e.y, e.z, WHITE, 0.5 + 0.3 * k, 0.65 * k);
        // The cistern lit from within by it.
        this.glow.add(0, 0.1, 0, RED_SOFT, 2.2, 0.18 * k);
      }
    }
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** A flat ribbon through points (on their heights), `w` wide; its uv along (y) and across (x). */
const ribbon = (pts: THREE.Vector3[], w: number): THREE.BufferGeometry => {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  let len = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i] as THREE.Vector3;
    const a = (pts[Math.max(0, i - 1)] as THREE.Vector3).clone();
    const b = (pts[Math.min(pts.length - 1, i + 1)] as THREE.Vector3).clone();
    const dir = b.sub(a).setY(0).normalize();
    const nx = -dir.z;
    const nz = dir.x;
    if (i > 0) len += p.distanceTo(pts[i - 1] as THREE.Vector3);
    pos.push(p.x + (nx * w) / 2, p.y, p.z + (nz * w) / 2, p.x - (nx * w) / 2, p.y, p.z - (nz * w) / 2);
    uv.push(0, len, 1, len);
    if (i > 0) {
      const k = i * 2;
      idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/** Merges non-indexed geometries with the same attributes. */
const mergeAll = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry | null => {
  let n = 0;
  for (const p of parts) n += (p.getAttribute('position') as THREE.BufferAttribute).count;
  if (!n) return null;
  const pos = new Float32Array(n * 3);
  let o = 0;
  for (const p of parts) {
    const a = p.getAttribute('position') as THREE.BufferAttribute;
    pos.set(a.array as Float32Array, o * 3);
    o += a.count;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  return g;
};
